export interface WorksCategory {
  id: number
  name: string
  slug: string
}

export interface WorksTag {
  id: number
  name: string
  slug: string
}

export interface WorksImage {
  ID?: number
  id?: number
  url?: string
  alt?: string
  title?: string
  caption?: string
  sizes?: {
    thumbnail?: string
    medium?: string
    large?: string
    full?: string
    [key: string]: string | undefined
  }
  width?: number
  height?: number
}

export interface WorksVideo {
  ID?: number
  id?: number
  title?: string
  filename?: string
  filesize?: number
  url?: string
  link?: string
  alt?: string
  author?: string
  description?: string
  caption?: string
  name?: string
  status?: string
  uploaded_to?: number
  date?: string
  modified?: string
  menu_order?: number
  mime_type?: string
  type?: string
  subtype?: string
  icon?: string
  width?: number
  height?: number
}

export interface WorksTextContent {
  acf_fc_layout: 'text'
  chars: string
}

export interface WorksImageContent {
  acf_fc_layout: 'image'
  source: WorksImage
  caption: string
}

export interface WorksVideoContent {
  acf_fc_layout: 'video'
  source: WorksVideo
  caption: string
}

export type WorksContent = WorksTextContent | WorksImageContent | WorksVideoContent

export interface StaffPost {
  ID: number
  post_title: string
  post_name?: string
  post_type?: string
  guid?: string
  acf?: {
    role?: string[] | string
    link?: string
    [key: string]: any
  }
  [key: string]: any
}

export interface CustomStaff {
  custom_role?: string[] | string
  custom_link?: string
}

export interface WorksCreditRaw {
  staff_post?: StaffPost | number | null
  enable_custom?: boolean | number | string
  custom_staff?: CustomStaff
  custom_role?: string[] | string
  custom_link?: string
  name?: string | any
  kindof?: string[] | string
  link?: string
}

export interface WorksCredit {
  role: string
  name: string
  link: string
  /** @deprecated 旧仕様互換（role と同値） */
  kindof: string
  staff_post?: StaffPost | number | null
  enable_custom?: boolean | number | string
  custom_staff?: CustomStaff
  custom_role?: string[] | string
  custom_link?: string
}

export interface GroupedWorksCreditItem {
  name: string
  link: string
}

export interface GroupedWorksCredit {
  role: string
  items: GroupedWorksCreditItem[]
}

export function groupWorksCredits(credits?: WorksCredit[]): GroupedWorksCredit[] {
  if (!credits || credits.length === 0) return []

  const groups: GroupedWorksCredit[] = []
  for (const credit of credits) {
    if (!credit.name && !credit.role) continue
    const existing = groups.find(g => g.role === credit.role)
    if (existing) {
      existing.items.push({
        name: credit.name,
        link: credit.link
      })
    } else {
      groups.push({
        role: credit.role,
        items: [{
          name: credit.name,
          link: credit.link
        }]
      })
    }
  }
  return groups
}

export interface WorksItem {
  id: number
  title: string
  slug: string
  date: string
  modified: string
  link: string
  measures: WorksCategory[]
  featuredImage?: string
  description?: string
  contents?: WorksContent[]
  credits?: WorksCredit[]
  thumbnailUrl: string
  client: string
  business: WorksCategory[]
}

export function getWorksImageSrc(image?: WorksImage): string {
  if (!image) return ''
  if (image.sizes && image.sizes.large) return image.sizes.large
  return image.url || ''
}

export function getWorksVideoSrc(video?: WorksVideo): string {
  if (!video) return ''
  return video.url || video.link || ''
}

export function findFirstWorksImage(contents?: WorksContent[]): WorksImage | undefined {
  if (!contents) return undefined
  const imageContent = contents.find((content): content is WorksImageContent => {
    return content.acf_fc_layout === 'image' && !!(content as WorksImageContent).source
  })
  return imageContent ? imageContent.source : undefined
}

export function findFirstWorksVideo(contents?: WorksContent[]): WorksVideo | undefined {
  if (!contents) return undefined
  const videoContent = contents.find((content): content is WorksVideoContent => {
    return content.acf_fc_layout === 'video' && !!(content as WorksVideoContent).source
  })
  return videoContent ? videoContent.source : undefined
}

export function formatWorksItem(post: any): WorksItem {
  const embedded = post._embedded || {}
  const terms = embedded['wp:term'] || []
  const measures: any[] = []
  const client: any[] = []
  const business: any[] = []

  terms.forEach((group: any[]) => {
    if (!group || group.length === 0) return
    const taxonomy = group[0].taxonomy
    if (taxonomy === 'measures') {
      measures.push(...group)
    } else if (taxonomy === 'client') {
      client.push(...group)
    } else if (taxonomy === 'business') {
      business.push(...group)
    }
  })

  const featuredImage = embedded['wp:featuredmedia'] && embedded['wp:featuredmedia'][0]
    ? embedded['wp:featuredmedia'][0].source_url
    : ''

  const contents: WorksContent[] = (post.acf && post.acf.contents) || []
  const firstContentImage = findFirstWorksImage(contents)
  const thumbnailUrl = featuredImage || getWorksImageSrc(firstContentImage) || ''

  // _embedded から staff 投稿のマップを作成
  const staffMap = new Map<number, any>()
  if (embedded) {
    Object.values(embedded).forEach((val: any) => {
      if (Array.isArray(val)) {
        val.forEach((item: any) => {
          if (Array.isArray(item)) {
            item.forEach((sub: any) => {
              if (sub && (sub.type === 'staff' || sub.id)) {
                staffMap.set(Number(sub.id), sub)
              }
            })
          } else if (item && (item.type === 'staff' || item.id)) {
            staffMap.set(Number(item.id), item)
          }
        })
      }
    })
  }

  const rawCredits = (post.acf && post.acf.credits) || []
  const credits: WorksCredit[] = rawCredits.map((c: any) => {
    const isCustom = c.enable_custom === true || c.enable_custom === 1 || c.enable_custom === '1' || c.enable_custom === 'true'
    const staffId = typeof c.staff_post === 'object' && c.staff_post ? (c.staff_post.ID || c.staff_post.id) : Number(c.staff_post)
    const embeddedStaff = staffId ? staffMap.get(Number(staffId)) : null

    // 役割の取得（enable_custom が真なら custom_role、偽ならマスターの role を参照）
    let role = ''
    let rawRole: any = ''
    if (isCustom) {
      const customRole = (c.custom_staff && c.custom_staff.custom_role !== undefined)
        ? c.custom_staff.custom_role
        : c.custom_role
      rawRole = (customRole !== undefined && customRole !== null) ? customRole : ''
    } else {
      rawRole = (embeddedStaff && embeddedStaff.acf && embeddedStaff.acf.role)
        || (embeddedStaff && embeddedStaff.role)
        || (c.staff_post && c.staff_post.role)
        || (c.staff_post && c.staff_post.acf && c.staff_post.acf.role)
        || c.role || c.kindof || ''
    }

    if (Array.isArray(rawRole)) {
      role = rawRole.filter(Boolean).join(' / ')
    } else if (typeof rawRole === 'string') {
      role = rawRole.trim()
    }

    // スタッフ名の取得（staff_post を優先、なければ embeddedStaff を参照）
    let name = ''
    if (c.staff_post && typeof c.staff_post === 'object') {
      name = c.staff_post.post_title || (c.staff_post.title && c.staff_post.title.rendered) || c.staff_post.name || ''
    } else if (embeddedStaff) {
      name = (embeddedStaff.title && embeddedStaff.title.rendered) || embeddedStaff.name || ''
    } else if (typeof c.staff_post === 'string') {
      name = c.staff_post
    } else if (typeof c.name === 'string') {
      name = c.name
    }

    // リンクの取得（enable_custom が真なら custom_link、偽ならマスターの acf.link を参照）
    let link = ''
    if (isCustom) {
      const customLink = (c.custom_staff && typeof c.custom_staff.custom_link === 'string')
        ? c.custom_staff.custom_link
        : c.custom_link
      if (typeof customLink === 'string') {
        link = customLink.trim()
      }
    } else {
      const masterLink = (embeddedStaff && embeddedStaff.acf && typeof embeddedStaff.acf.link === 'string')
        ? embeddedStaff.acf.link.trim()
        : (c.staff_post && c.staff_post.acf && typeof c.staff_post.acf.link === 'string')
        ? c.staff_post.acf.link.trim()
        : (typeof c.link === 'string' ? c.link.trim() : '')
      if (typeof masterLink === 'string') {
        link = masterLink.trim()
      }
    }

    return {
      role,
      name,
      link,
      kindof: role,
      staff_post: c.staff_post,
      enable_custom: c.enable_custom,
      custom_staff: c.custom_staff,
      custom_role: c.custom_role,
      custom_link: c.custom_link
    }
  })

  return {
    id: post.id,
    title: post.title && post.title.rendered ? post.title.rendered : '',
    slug: post.slug,
    date: post.date,
    modified: post.modified,
    link: post.link,
    measures: measures.map((c: any) => ({ id: c.id, name: c.name, slug: c.slug })),
    featuredImage,
    description: post.acf && post.acf.description ? post.acf.description : '',
    contents,
    credits,
    thumbnailUrl,
    client: client.map((t: any) => t.name).join(' / '),
    business: business.map((c: any) => ({ id: c.id, name: c.name, slug: c.slug }))
  }
}
